/**
 * Web purchases with Stripe Checkout. purchase() asks the API for a Checkout
 * Session (the API applies the MO-06 guards there) and navigates to it; the page
 * unloads, so it resolves `pending`. Stripe returns to
 * `?checkout=success&product=…&idem=…&session_id=…`; resume() then asks the API to
 * verify the session (granted, or pending until the payment settles).
 */
import { conflictBody, ROUTES, type ApiClient } from '../api.js';
import { randomId } from '../lazy.js';
import type { CheckoutRequest, CheckoutSession, PurchaseResponse, PurchaseVerify, StoreResponse } from '../schemas.js';
import { confirmFlag, fromResponse, withUsdPrice, type IapProduct, type IapProvider, type PurchaseOptions, type PurchaseResult } from './types.js';

export interface StripeIapOptions {
  api: ApiClient;
  /** page to come back to, e.g. `${origin}/store` */
  returnUrl: string;
  /** location.assign */
  navigate?: (url: string) => void;
  /** location.href at startup */
  currentUrl?: () => string;
  /** history.replaceState, to drop the checkout params once handled */
  replaceUrl?: (url: string) => void;
  newKey?: () => string;
}

const CHECKOUT_PARAMS = ['checkout', 'product', 'idem', 'session_id'];

/** 409 bodies of the checkout's MO-06 guards (parental block, over-the-daily-limit confirmation). */
const GUARD_CODES = new Set(['parental_consent_required', 'confirm_required']);
const isGuardBlock = (b: unknown): b is { error: string } => GUARD_CODES.has(String((b as { error?: unknown } | null)?.error));

export class StripeIapProvider implements IapProvider {
  readonly provider = 'stripe';

  constructor(private readonly o: StripeIapOptions) {}

  async products(): Promise<IapProduct[]> {
    const store = await this.o.api.call<StoreResponse>('GET', ROUTES.store);
    return store.products.map(withUsdPrice);
  }

  async purchase(productId: string, opts: PurchaseOptions = {}): Promise<PurchaseResult> {
    const idemKey = (this.o.newKey ?? randomId)();
    const back = (status: string) => {
      const u = new URL(this.o.returnUrl);
      u.searchParams.set('checkout', status);
      u.searchParams.set('product', productId);
      u.searchParams.set('idem', idemKey);
      return u.toString();
    };
    // Stripe substitutes the literal {CHECKOUT_SESSION_ID}; URL APIs would escape the braces.
    // The same idemKey reaches the webhook (session metadata) and resume(), so the grant happens once.
    const body: CheckoutRequest = {
      productId,
      successUrl: `${back('success')}&session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: back('cancel'),
      idemKey,
      ...confirmFlag(opts),
    };
    const session = await this.o.api.call<CheckoutSession>('POST', ROUTES.stripeCheckout, body).catch(conflictBody(isGuardBlock));
    if ('error' in session) return { status: 'blocked', provider: this.provider, productId, reason: session.error };
    (this.o.navigate ?? ((url) => location.assign(url)))(session.url);
    return { status: 'pending', provider: this.provider, productId, receipt: session.id };
  }

  /** Stripe purchases are server records; restore = the next snapshot. */
  async restore(): Promise<PurchaseResult[]> {
    return [];
  }

  async resume(): Promise<PurchaseResult | null> {
    const url = new URL((this.o.currentUrl ?? (() => location.href))());
    const status = url.searchParams.get('checkout');
    const productId = url.searchParams.get('product');
    if (!status || !productId) return null;
    const sessionId = url.searchParams.get('session_id');
    const idemKey = url.searchParams.get('idem') ?? sessionId ?? randomId();
    for (const p of CHECKOUT_PARAMS) url.searchParams.delete(p);
    (this.o.replaceUrl ?? ((u) => history.replaceState(history.state, '', u)))(url.toString());
    if (status !== 'success' || !sessionId) return { status: 'cancelled', provider: this.provider, productId };
    // A session exists only if the checkout passed the MO-06 guards and the player has paid,
    // so the verify carries the confirmation, as the webhook grant does.
    const body: PurchaseVerify = { provider: 'stripe', productId, receipt: sessionId, idemKey, confirm: true };
    const res = await this.o.api.call<PurchaseResponse>('POST', ROUTES.purchaseVerify, body);
    return fromResponse('stripe', productId, res, sessionId);
  }
}
